"use client";

import React, { useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { borderColorTertiary, backgroundColorLevelCurrent, textColorPrimary } from "@churchofjesuschrist/eden-style-constants";
import { useClickBoundary } from "@churchofjesuschrist/eden-click-boundary";
import Card from "@churchofjesuschrist/eden-card";
import { createPopper } from "@popperjs/core";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const StyledCallout = styled(Card).withConfig({
  componentId: "sc-yoesbz-0"
})(["--calloutBackgroundColor:", ";--calloutBorderColor:", ";display:block;color:", ";position:absolute;min-width:min-content;max-width:19rem;visibility:hidden;opacity:0;z-index:3;padding:0;margin:0;&:before{display:block;content:\"\";height:11px;top:-11px;width:100%;position:absolute;[data-popper-placement^=\"top\"]&{top:auto;bottom:-11px;}}&[data-popper-placement]{opacity:1;}&[open]{visibility:visible;}"], backgroundColorLevelCurrent, borderColorTertiary, textColorPrimary);
const Arrow = styled.div.withConfig({
  componentId: "sc-yoesbz-1"
})(["position:absolute;&:before,&:after{border:solid transparent;content:\" \";height:0;width:0;position:absolute;z-index:4;bottom:100%;left:50%;}[data-popper-placement] &{bottom:0;&:after{border-top-color:var(--calloutBackgroundColor);border-width:9px;margin-left:-8px;border-bottom-width:0;}&:before{border-top-color:var(--calloutBorderColor);border-width:11px;margin-left:-10px;border-bottom-width:0;}}[data-popper-placement^=\"top\"] &{&:before,&:after{top:100%;}}[data-popper-placement^=\"bottom\"] &{bottom:100%;&:after,&:before{transform:rotate(-180deg);bottom:0;top:auto;}}"]);

// This is to suppress warnings related to not being able to use the `autoFocus` prop due to this bug: https://github.com/facebook/react/issues/23301
if (typeof console !== "undefined" && typeof console.error === "function") {
  const consoleError = console.error;
  console.error = function filterWarnings(...args) {
    if (!args.includes("autoFocus")) {
      consoleError(...args);
    }
  };
}

/**
 * A callout (also known as, popover and/or popup) is a fly-out container that displays contextual information or available actions. It appears next to the relevant element and may contain actions related to it. Use the Callout to add contextual information to explain a specific item or to provide an area for advanced interactions.
 * @param {object} props - The component props
 * @param {object} props.forRef - The ref to which the Callout belongs
 * @param {React.ReactNode} props.children - Content inside the Callout
 * @param {boolean} [props.open] - Whether the Callout is open
 * @param {'top' | 'bottom'} [props.placement] - Default location of the Callout relative to the `forRef`. Will flip if there is not enough available space in that direction.
 * @param {Function} [props.onClickOutside] - Handler for when a click occurs outside of the Callout (e.g., to close the Callout)
 * @returns {React.ReactElement} The Callout element
 */
const Callout = ({
  forRef,
  open = false,
  onClickOutside,
  children,
  placement = "bottom",
  ...props
}) => {
  const arrowRef = useRef(null);
  const onClickOutsideInternal = useCallback(event => {
    if (!open) {
      return;
    }
    requestAnimationFrame(() => {
      onClickOutside?.(event);
    });
  }, [open, onClickOutside]);
  const callout = useClickBoundary({
    onClickInside: x => x,
    onClickOutside: onClickOutsideInternal
  });
  useEffect(() => {
    if (open) {
      const handleEsc = event => {
        if (event.code === "Escape") {
          onClickOutsideInternal(event);
        }
      };
      document.addEventListener("keyup", handleEsc, {
        capture: true
      });
      return () => document.removeEventListener("keyup", handleEsc, {
        capture: true
      });
    }
  }, [open, onClickOutsideInternal]);
  useEffect(() => {
    if (forRef.current && callout.current && open) {
      callout.current.show?.();
      const popperInstance = createPopper(forRef.current, callout.current, {
        placement,
        modifiers: [{
          name: "arrow",
          options: {
            element: arrowRef.current
          }
        }, {
          name: "offset",
          options: {
            offset: [0, 10]
          }
        }]
      });
      return () => {
        popperInstance.destroy();
      };
    }
  }, [callout, forRef, open, placement]);
  return open ? /*#__PURE__*/_jsxs(StyledCallout, {
    renderAs: "dialog",
    depth: "overlaid",
    ...props,
    ref: callout,
    children: [/*#__PURE__*/_jsx(Arrow, {
      ref: arrowRef
    }), children]
  }) : null;
};
export default Callout;